import { Flex, Button, Box, Text } from "@chakra-ui/react";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { getCustomButtonVariant } from "utils/uiconfighelper";

export interface PaginationProps {
  totalPages: number;
  currentPage: number;
  handlePageChange: (page: number) => void;
  totalCount: number;
  pageSize: number;
}

export function Pagination({
  totalPages,
  currentPage,
  handlePageChange,
  totalCount: totalRows,
  pageSize,
}: PaginationProps) {
  const { user } = useTenant();

  const getPageRange = (currentPage: number, pageSize: number, totalRows: number) => {
    const startRow = (currentPage - 1) * pageSize + 1;
    const endRow = Math.min(currentPage * pageSize, totalRows);
    return `${startRow}-${endRow}`;
  };

  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show before and after current page
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // First page
        i === totalPages || // Last page
        (i >= currentPage - delta && i <= currentPage + delta) // Pages around current page
      ) {
        range.push(i);
      }
    }

    for (let i = 0; i < range.length; i++) {
      if (l) {
        if (range[i] - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (range[i] - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(range[i]);
      l = range[i];
    }

    return rangeWithDots;
  };

  if (totalRows === 0) {
    return (
      <Flex justifyContent="space-between">
        <Box>
          <Text>No results found</Text>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex justifyContent="space-between" alignItems="center" gap={2}>
      <Box>
        {totalRows > 0 ? (
          <Text>
            Showing {getPageRange(currentPage, pageSize, totalRows)} items of {totalRows}
          </Text>
        ) : null}
      </Box>
      <Flex gap={1} alignItems="center">
        {getVisiblePages().map((page, index) =>
          page === "..." ? (
            <Text key={`dots-${index}`}>...</Text>
          ) : (
            <Button
              border="none"
              fontSize="14px"
              lineHeight="14px"
              padding={0}
              key={index}
              onClick={() => handlePageChange(page as number)}
              variant={currentPage === page ? getCustomButtonVariant(user) : "ghost"}
              borderRadius="100%"
              size="sm"
            >
              {page}
            </Button>
          )
        )}
      </Flex>
    </Flex>
  );
}
